import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, Trash2, Edit, Search, FileText, Share2, Eye, Network } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ShareModal from '../components/ShareModal';
import './Vpn.css';

interface Vpn {
  id: string;
  name: string;
  description?: string;
  serverAddress?: string;
  protocol?: string;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  permissions: {
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
}

const Vpn: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [vpns, setVpns] = useState<Vpn[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVpn, setEditingVpn] = useState<Vpn | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    tags: [] as string[],
    notes: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingVpn, setSharingVpn] = useState<Vpn | null>(null);

  const fetchVpns = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vpn?${searchTerm ? `search=${encodeURIComponent(searchTerm)}` : ''}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVpns(data);
      }
    } catch (error) {
      console.error('Failed to fetch VPNs:', error);
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm]);

  useEffect(() => {
    fetchVpns();
  }, [fetchVpns]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!loading) {
        fetchVpns();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, loading, fetchVpns]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.tags.length > 0) formDataToSend.append('tags', JSON.stringify(formData.tags));
      if (formData.notes) formDataToSend.append('notes', formData.notes);

      if (uploadFile) {
        formDataToSend.append('file', uploadFile);
      } else if (formData.content) {
        formDataToSend.append('content', formData.content);
      } else {
        setError('Please provide VPN config content (file upload or paste)');
        return;
      }

      const url = editingVpn
        ? `${process.env.REACT_APP_API_URL || '/api'}/vpn/${editingVpn.id}`
        : `${process.env.REACT_APP_API_URL || '/api'}/vpn`;

      const method = editingVpn ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ name: '', description: '', content: '', tags: [], notes: '' });
        setUploadFile(null);
        setEditingVpn(null);
        fetchVpns();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save VPN');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to save VPN');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this VPN?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vpn/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchVpns();
      }
    } catch (error) {
      console.error('Failed to delete VPN:', error);
    }
  };

  const handleDownload = async (id: string, name: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vpn/${id}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name.replace(/[^a-z0-9]/gi, '_')}_vpn.ovpn`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to download VPN:', error);
    }
  };

  const handleEdit = (vpn: Vpn) => {
    setEditingVpn(vpn);
    setFormData({
      name: vpn.name,
      description: vpn.description || '',
      content: '',
      tags: vpn.tags || [],
      notes: vpn.notes || '',
    });
    setUploadFile(null);
    setShowModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
      setFormData({ ...formData, content: '' });
    }
  };

  if (loading) {
    return <div className="loading">Loading VPNs...</div>;
  }

  return (
    <div className="vpn-page">
      <div className="page-header">
        <h1>VPN Management</h1>
        {user?.role !== 'STANDARD_USER' && (
          <button className="btn-primary" onClick={() => {
            setEditingVpn(null);
            setFormData({ name: '', description: '', content: '', tags: [], notes: '' });
            setUploadFile(null);
            setShowModal(true);
          }}>
            <Plus size={20} />
            Add VPN
          </button>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#3b82f6' }}>
            <Network size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{vpns.length}</div>
            <div className="stat-label">Total VPNs</div>
          </div>
        </div>
      </div>

      <div className="search-bar">
        <Search size={20} />
        <input
          type="text"
          placeholder="Search VPNs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="vpn-grid">
        {vpns.map((vpn) => (
          <div key={vpn.id} className="vpn-card">
            <div className="card-header">
              <h3>{vpn.name}</h3>
              <div className="card-actions">
                {vpn.permissions.canView && (
                  <>
                    <button
                      className="icon-btn"
                      onClick={() => navigate(`/vpn/${vpn.id}`)}
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => {
                        setSharingVpn(vpn);
                        setShowShareModal(true);
                      }}
                      title="Share"
                    >
                      <Share2 size={18} />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => handleDownload(vpn.id, vpn.name)}
                      title="Download"
                    >
                      <Download size={18} />
                    </button>
                  </>
                )}
                {vpn.permissions.canEdit && (
                  <button
                    className="icon-btn"
                    onClick={() => handleEdit(vpn)}
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                )}
                {vpn.permissions.canDelete && (
                  <button
                    className="icon-btn danger"
                    onClick={() => handleDelete(vpn.id)}
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
            {vpn.description && <p className="card-description">{vpn.description}</p>}
            <div className="card-info">
              {vpn.serverAddress && (
                <div className="info-item">
                  <strong>Server:</strong> {vpn.serverAddress}
                </div>
              )}
              {vpn.protocol && (
                <div className="info-item">
                  <strong>Protocol:</strong> {vpn.protocol}
                </div>
              )}
              <div className="info-item">
                <strong>Created by:</strong> {vpn.createdBy.username}
              </div>
              <div className="info-item">
                <strong>Updated:</strong> {new Date(vpn.updatedAt).toLocaleDateString()}
              </div>
            </div>
            {vpn.tags.length > 0 && (
              <div className="card-tags">
                {vpn.tags.map((tag, idx) => (
                  <span key={idx} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {vpns.length === 0 && (
        <div className="empty-state">
          <FileText size={48} />
          <p>No VPNs found</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingVpn ? 'Edit VPN' : 'Add VPN'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Upload File or Paste Content</label>
                <input
                  type="file"
                  accept=".ovpn,.conf,.txt"
                  onChange={handleFileChange}
                  disabled={!!formData.content}
                />
                {!uploadFile && (
                  <textarea
                    placeholder="Or paste VPN config content here..."
                    value={formData.content}
                    onChange={(e) => {
                      setFormData({ ...formData, content: e.target.value });
                      setUploadFile(null);
                    }}
                    rows={10}
                    style={{ marginTop: '10px', fontFamily: 'monospace' }}
                  />
                )}
                {uploadFile && (
                  <div className="file-info">
                    <FileText size={16} />
                    <span>{uploadFile.name}</span>
                    <button
                      type="button"
                      onClick={() => setUploadFile(null)}
                      className="btn-link"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingVpn ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showShareModal && sharingVpn && (
        <ShareModal
          vpnId={sharingVpn.id}
          vpnName={sharingVpn.name}
          resourceType="vpn"
          onClose={() => {
            setShowShareModal(false);
            setSharingVpn(null);
          }}
          onSuccess={() => {
            fetchVpns();
          }}
        />
      )}
    </div>
  );
};

export default Vpn;

